/**
 * HEPATIC RESOLVER (MEV5)
 * Mechanism: Hepatic Glucose Production (HGP) & Gluconeogenesis
 * Standard: UVA/Padova T1DM Simulator (2024)
 */

export const HepaticResolver = {
    
    /**
     * Calculates the grams of glucose released by the liver per minute.
     * 
     * MATH:
     * - Base HGP: ~0.1g/min (Maintains fasting glucose at 80-90 mg/dL)
     * - Suppression: Insulin suppresses HGP.
     * - Circadian Resistance: Fat + Late Night = Liver becomes resistant to insulin suppression.
     * 
     * @param currentInsulin Systemic insulin level
     * @param fatInBlood Current circulating lipids (triggers hepatic resistance)
     * @param circadianMod The current time-of-day metabolic modifier
     */
    compute(currentInsulin: number, fatInBlood: number, circadianMod: number): number {
        // Base production (Liver "Drip")
        const baseHGP = 0.1; 

        // Insulin Suppression (More insulin = less liver output)
        // K_suppress is tuned so that fasting insulin (10-15) suppresses HGP to ~0.08g/min
        const suppression = Math.max(0, 1 - (currentInsulin * 0.02));

        // Hepatic Insulin Resistance (Late Night + High Fat)
        // If it's night (circadianMod < 1.0) and fat is high, the liver ignores insulin suppression.
        let resistanceFactor = 1.0;
        if (circadianMod < 1.0 && fatInBlood > 2.0) {
            resistanceFactor = 1.0 + (fatInBlood * 0.1); 
        }

        // Final Output
        // The liver produces fuel, but never less than 0.
        return Math.max(0, baseHGP * suppression * resistanceFactor);
    }
};
